import { useEffect, useState } from "react";
import "./App.css";
import { getCurrentEmployee, loginEmployee } from "./api/instateamEmployeeApi";
import { INITIAL_FORM } from "./assets/instateamEmployeeConfig";
import EmployeeAccessDeniedPage from "./pages/EmployeeAccessDeniedPage";
import EmployeeAuthPage from "./pages/EmployeeAuthPage";
import EmployeePanelPage from "./pages/EmployeePanelPage";
import {
  applyEmployeeTheme,
  parseStoredSession,
  STORAGE_KEY,
} from "./utils/employeeHelpers";

const EMPLOYEE_VIEW_STORAGE_KEY = "instateam-employee-active-view";
const MOBILE_RESTRICTION_WIDTH = 992;

function shouldRestrictToDesktop() {
  if (typeof window === "undefined") return false;

  const nav = window.navigator || {};
  const ua = String(nav.userAgent || "").toLowerCase();
  const uaMobile =
    Boolean(nav.userAgentData?.mobile) ||
    /android|iphone|ipad|ipod|mobile|iemobile|opera mini|blackberry/i.test(ua);
  const viewportWidth = Number(window.innerWidth || 0);
  const screenWidth = Number(window.screen?.width || viewportWidth || 0);
  const screenHeight = Number(window.screen?.height || window.innerHeight || 0);
  const smallScreen = Math.min(screenWidth, screenHeight || screenWidth) <= MOBILE_RESTRICTION_WIDTH;
  const touchDevice = Number(nav.maxTouchPoints || 0) > 0;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  const noHover = window.matchMedia?.("(hover: none)")?.matches ?? false;

  return (
    uaMobile ||
    viewportWidth <= MOBILE_RESTRICTION_WIDTH ||
    (touchDevice && coarsePointer && smallScreen) ||
    (touchDevice && noHover && smallScreen)
  );
}

function DeviceRestrictedScreen() {
  return (
    <div className="device-restricted-screen">
      <section className="device-restricted-card" role="dialog" aria-modal="true">
        <div className="device-restricted-badge">PC</div>
        <h1>Restricted In Mobile</h1>
        <p>
          Insta Team employee panel is designed for laptop and desktop only.
          Open it on a PC or laptop to continue.
        </p>
        <small>Mobile browsers and desktop-site mode on mobile are not supported.</small>
      </section>
    </div>
  );
}

function DesktopApp() {
  const [session, setSession] = useState(parseStoredSession);
  const [isRestoringSession, setIsRestoringSession] = useState(() =>
    Boolean(parseStoredSession()?.token)
  );
  const [accessDeniedEmployee, setAccessDeniedEmployee] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    applyEmployeeTheme(session?.employee?.settings || {});
  }, [session?.employee?.settings]);

  useEffect(() => {
    if (!session?.token) {
      setIsRestoringSession(false);
      return;
    }

    let isActive = true;

    async function hydrateSession() {
      try {
        setIsRestoringSession(true);
        const payload = await getCurrentEmployee(session.token);

        if (!isActive) return;

        const nextSession = {
          token: session.token,
          employee: payload.employee,
        };

        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
        setSession(nextSession);
        setAccessDeniedEmployee(null);
      } catch (error) {
        if (!isActive) return;

        persistSession(null);

        if (error.status === 403 && error.code === "EMPLOYEE_DISABLED") {
          setAccessDeniedEmployee(error.data?.employee || null);
          setStatusMessage(null);
        } else {
          setStatusMessage({
            type: "error",
            text: error.message,
          });
        }
      } finally {
        if (isActive) {
          setIsRestoringSession(false);
        }
      }
    }

    hydrateSession();

    return () => {
      isActive = false;
    };
  }, [session?.token]);

  function persistSession(nextSession) {
    if (nextSession) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(EMPLOYEE_VIEW_STORAGE_KEY);
    }

    setSession(nextSession);
  }

  async function handleLogin(event) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setStatusMessage(null);

      const payload = await loginEmployee({
        username: form.username,
        password: form.password,
      });

      setAccessDeniedEmployee(null);
      persistSession({
        token: payload.token,
        employee: payload.employee,
      });

      setStatusMessage({
        type: "success",
        text: "Employee login successful.",
      });
    } catch (error) {
      if (error.status === 403 && error.code === "EMPLOYEE_DISABLED") {
        persistSession(null);
        setAccessDeniedEmployee(error.data?.employee || null);
        setStatusMessage(null);
      } else {
        setStatusMessage({
          type: "error",
          text: error.message,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleForgotPassword(event) {
    event.preventDefault();

    if (!form.forgotEmail.trim()) {
      setStatusMessage({
        type: "warning",
        text: "Enter your registered email address first.",
      });
      return;
    }

    setStatusMessage({
      type: "warning",
      text: "Password reset email flow is not configured yet. For now, please contact your administrator.",
    });
  }

  function handleLogoutRequest() {
    setIsLogoutConfirmOpen(true);
  }

  function handleLogoutCancel() {
    setIsLogoutConfirmOpen(false);
  }

  function handleLogoutConfirm() {
    persistSession(null);
    setAccessDeniedEmployee(null);
    setStatusMessage(null);
    setShowForgot(false);
    setIsLogoutConfirmOpen(false);
  }

  function handleEmployeeUpdate(nextEmployee) {
    if (!nextEmployee || !session?.token) return;
    persistSession({
      token: session.token,
      employee: nextEmployee,
    });
  }

  if (isRestoringSession) {
    return (
      <div className="employee-shell">
        <section className="employee-loading-card">
          <div className="loading-orb" />
          <h2>Loading your employee portal...</h2>
          <p>Checking your account access and preparing your workspace.</p>
        </section>
      </div>
    );
  }

  if (session?.employee) {
    return (
      <>
        <EmployeePanelPage
          employee={session.employee}
          token={session.token}
          onLogout={handleLogoutRequest}
          onEmployeeUpdate={handleEmployeeUpdate}
        />

        {isLogoutConfirmOpen ? (
          <div
            className="employee-modal-overlay"
            role="presentation"
            onClick={handleLogoutCancel}
          >
            <section
              className="employee-regularization-modal logout-confirm-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="employee-logout-confirm-title"
              onClick={(event) => event.stopPropagation()}
            >
              <h3 id="employee-logout-confirm-title">Logout from Insta Team?</h3>
              <p className="employee-modal-copy">
                Are you sure you want to logout from your employee panel?
              </p>

              <div className="logout-confirm-actions">
                <button type="button" className="secondary-button" onClick={handleLogoutCancel}>
                  Cancel
                </button>
                <button type="button" className="primary-button" onClick={handleLogoutConfirm}>
                  Yes, Logout
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </>
    );
  }

  if (accessDeniedEmployee) {
    return (
      <EmployeeAccessDeniedPage
        employee={accessDeniedEmployee}
        onBackToLogin={() => {
          setAccessDeniedEmployee(null);
          setStatusMessage(null);
          setShowForgot(false);
          setForm(INITIAL_FORM);
        }}
      />
    );
  }

  return (
    <EmployeeAuthPage
      form={form}
      showForgot={showForgot}
      showPassword={showPassword}
      isSubmitting={isSubmitting}
      statusMessage={statusMessage}
      onClearStatus={() => setStatusMessage(null)}
      onFormChange={setForm}
      onToggleForgot={setShowForgot}
      onTogglePassword={() => setShowPassword((value) => !value)}
      onLogin={handleLogin}
      onForgotPassword={handleForgotPassword}
    />
  );
}

export default function App() {
  const [isMobileRestricted, setIsMobileRestricted] = useState(() =>
    shouldRestrictToDesktop()
  );

  useEffect(() => {
    const updateRestrictionState = () => {
      setIsMobileRestricted(shouldRestrictToDesktop());
    };

    updateRestrictionState();
    window.addEventListener("resize", updateRestrictionState);
    window.addEventListener("orientationchange", updateRestrictionState);

    return () => {
      window.removeEventListener("resize", updateRestrictionState);
      window.removeEventListener("orientationchange", updateRestrictionState);
    };
  }, []);

  if (isMobileRestricted) {
    return <DeviceRestrictedScreen />;
  }

  return <DesktopApp />;
}
